"use client";

import { Button } from "@nudj-digital/design-system/components/ui/button";
import { Input } from "@nudj-digital/design-system/components/ui/input";
import { Label } from "@nudj-digital/design-system/components/ui/label";
import { cn } from "@nudj-digital/design-system/lib/utils";
import { AnimatePresence, motion, useAnimation } from "framer-motion";
import { AlertCircle, ArrowLeft, Loader2, Mail } from "lucide-react";
import Image from "next/image";
import { signIn } from "next-auth/react";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { logger } from "@/lib/logger";

interface AuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type AuthStep = "options" | "email" | "otp";

// Shake animation for error feedback
const shakeAnimation = {
  x: [0, -10, 10, -10, 10, 0],
  transition: { duration: 0.4 },
};

export function AuthScreen() {
  const [email, setEmail] = useState("");
  const [sentToEmail, setSentToEmail] = useState<string | null>(null);
  const [step, setStep] = useState<AuthStep>("options");
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isEmailInvalid, setIsEmailInvalid] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(
    null
  );
  const [isRateLimited, setIsRateLimited] = useState(false);
  const otpInputControls = useAnimation();

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer((t) => t - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  const validateEmail = (email: string): boolean =>
    /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,63}$/i.test(email);
  const isInvalidEmail = email !== "" && !validateEmail(email);

  const onEmailBlur = (email: string) => {
    const isValid = validateEmail(email);
    setIsEmailInvalid(!isValid);
  };

  const handleSendOtp = async () => {
    if (isInvalidEmail || !email || isSubmitting) return;

    // If same email and timer still active, just go back to OTP step
    // Use lowercase comparison since emails are case-insensitive
    if (email.toLowerCase() === sentToEmail && timer > 0) {
      setStep("otp");
      return;
    }

    setSubmitError(null);
    setIsSubmitting(true);

    try {
      const result = await signIn("email", {
        callbackUrl: "/welcome",
        email,
        redirect: false,
      });

      if (result?.error) {
        logger.error("OTP send failed", { error: result.error });
        setSubmitError("Failed to send verification code. Please try again.");
        setIsSubmitting(false);
        return;
      }

      // Success - move to OTP step
      // Store lowercase for case-insensitive comparison
      setSentToEmail(email.toLowerCase());
      setStep("otp");
      setTimer(60);
    } catch (error) {
      logger.error("OTP send error", {
        error:
          error instanceof Error
            ? { name: error.name, message: error.message, stack: error.stack }
            : { message: String(error) },
      });
      setSubmitError("Failed to send verification code. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6 || isVerifying) return;

    setSubmitError(null);
    setIsVerifying(true);

    try {
      const response = await fetch("/api/auth/verify-otp", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          code: otp,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Redirect to NextAuth callback to complete sign-in
        // NextAuth handles token consumption and session creation
        // Keep isVerifying=true to prevent re-clicks during redirect
        const params = new URLSearchParams({
          callbackUrl: "/welcome",
          token: otp,
          email,
        });
        window.location.replace(
          `/api/auth/callback/email?${params.toString()}`
        );
        return;
      }

      // Handle error response - reset verifying state to allow retry
      setIsVerifying(false);
      setSubmitError(data.message);
      if (data.remainingAttempts !== undefined) {
        setRemainingAttempts(data.remainingAttempts);
      }

      // Handle rate limiting - block the UI
      if (data.error === "rate_limited") {
        setIsRateLimited(true);
        return;
      }

      // Shake animation and clear input on error
      await otpInputControls.start(shakeAnimation);
      setOtp("");
    } catch (error) {
      // Reset verifying state on error to allow retry
      setIsVerifying(false);
      logger.error("OTP verification error", {
        error:
          error instanceof Error
            ? { name: error.name, message: error.message }
            : { message: String(error) },
      });
      setSubmitError("Verification failed. Please try again.");
      await otpInputControls.start(shakeAnimation);
      setOtp("");
    }
  };

  // Clear error when user starts typing OTP and auto-submit when complete
  const handleOtpChange = (value: string) => {
    setOtp(value);
    if (submitError) {
      setSubmitError(null);
    }
  };

  // Auto-submit when OTP is complete
  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentionally only trigger on otp change
  useEffect(() => {
    if (otp.length === 6 && !isVerifying && !isRateLimited && !submitError) {
      handleVerifyOtp();
    }
  }, [otp]);

  const onBackToOptions = () => {
    setStep("options");
    setEmail("");
    setOtp("");
    setIsEmailInvalid(false);
    setSubmitError(null);
    setTimer(0);
    setRemainingAttempts(null);
    setIsRateLimited(false);
  };

  const onBackToEmail = () => {
    setStep("email");
    setOtp("");
    setSubmitError(null);
    // Don't reset timer - preserve it so we can resume if same email is used
    setRemainingAttempts(null);
    setIsRateLimited(false);
  };

  return (
    <div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.5 }}
      >
        <div className="space-y-6">
          <AnimatePresence mode="wait">
            {step === "options" && (
              <motion.div
                key="signin-options"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-3"
              >
                <Button
                  onClick={() => setStep("email")}
                  className="h-12 w-full bg-primary hover:bg-primary/90"
                  size="lg"
                  type="button"
                >
                  <Mail className="mr-2 h-4 w-4" />
                  Continue with Email
                </Button>

                <div className="flex items-center gap-3 my-3">
                  <div className="flex-1 border-t border-white/10" />
                  <span className="text-xs text-white/40">or</span>
                  <div className="flex-1 border-t border-white/10" />
                </div>

                <Button
                  variant="outline"
                  onClick={() => signIn("google", { callbackUrl: "/welcome" })}
                  className="h-12 w-full border-white/10 bg-white/5 text-white hover:bg-white/10"
                  type="button"
                >
                  <svg
                    className="mr-2 h-5 w-5"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  Continue with Google
                </Button>

                <Button
                  variant="outline"
                  onClick={() =>
                    signIn("azure-ad", { callbackUrl: "/welcome" })
                  }
                  className="h-12 w-full border-white/10 bg-white/5 text-white hover:bg-white/10"
                  type="button"
                >
                  <svg
                    className="mr-2 h-5 w-5"
                    viewBox="0 0 23 23"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M1 1h10v10H1V1zm11 0h10v10H12V1zM1 12h10v10H1V12zm11 0h10v10H12V12z" />
                  </svg>
                  Continue with Microsoft
                </Button>
              </motion.div>
            )}

            {step === "email" && (
              <motion.div
                key="email-form"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div className="space-y-2">
                  <Label
                    htmlFor="email"
                    className="text-sm font-medium text-white/80"
                  >
                    Email address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={(e) => onEmailBlur(e.target.value)}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        !isInvalidEmail &&
                        email !== ""
                      ) {
                        handleSendOtp();
                      }
                    }}
                    disabled={isSubmitting}
                    className={cn(
                      "h-12 border-white/10 bg-white/5 text-white placeholder:text-white/40 hover:bg-white/10",
                      isInvalidEmail && "border-red-500/50"
                    )}
                  />
                  {isEmailInvalid && (
                    <p className="text-xs text-red-400">
                      Please enter a valid email address
                    </p>
                  )}
                </div>

                <Button
                  onClick={handleSendOtp}
                  disabled={
                    !email || isInvalidEmail || isEmailInvalid || isSubmitting
                  }
                  className="h-12 w-full bg-primary hover:bg-primary/90"
                  size="lg"
                  type="button"
                >
                  {isSubmitting ? "Sending..." : "Continue with Email"}
                </Button>
                {submitError && (
                  <p className="text-xs text-red-400">{submitError}</p>
                )}

                <Button
                  variant="ghost"
                  onClick={onBackToOptions}
                  className="h-10 w-full text-white/60 hover:bg-white/5 hover:text-white"
                  type="button"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to options
                </Button>
              </motion.div>
            )}

            {step === "otp" && (
              <motion.div
                key="otp-form"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className="text-center space-y-2">
                  <p className="text-sm text-white/80">
                    Enter the 6-digit code sent to
                  </p>
                  <p className="text-sm font-medium text-white">{email}</p>
                </div>

                <div className="flex flex-col items-center gap-3">
                  <motion.div animate={otpInputControls} className="relative">
                    <InputOTP
                      maxLength={6}
                      value={otp}
                      onChange={handleOtpChange}
                      disabled={isVerifying || isRateLimited}
                      autoFocus
                    >
                      <InputOTPGroup>
                        <InputOTPSlot
                          index={0}
                          className={submitError ? "border-red-500/50" : ""}
                        />
                        <InputOTPSlot
                          index={1}
                          className={submitError ? "border-red-500/50" : ""}
                        />
                        <InputOTPSlot
                          index={2}
                          className={submitError ? "border-red-500/50" : ""}
                        />
                        <InputOTPSlot
                          index={3}
                          className={submitError ? "border-red-500/50" : ""}
                        />
                        <InputOTPSlot
                          index={4}
                          className={submitError ? "border-red-500/50" : ""}
                        />
                        <InputOTPSlot
                          index={5}
                          className={submitError ? "border-red-500/50" : ""}
                        />
                      </InputOTPGroup>
                    </InputOTP>
                    {/* Loading overlay */}
                    {isVerifying && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg"
                      >
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                      </motion.div>
                    )}
                  </motion.div>

                  {/* Error message */}
                  {submitError && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-2 text-sm text-red-400"
                    >
                      <AlertCircle className="h-4 w-4" />
                      <span>{submitError}</span>
                    </motion.div>
                  )}

                  {/* Remaining attempts */}
                  {remainingAttempts !== null &&
                    remainingAttempts > 0 &&
                    remainingAttempts < 5 && (
                      <p className="text-xs text-white/60">
                        {remainingAttempts} attempt
                        {remainingAttempts !== 1 ? "s" : ""} remaining
                      </p>
                    )}
                </div>

                <Button
                  onClick={handleVerifyOtp}
                  disabled={otp.length !== 6 || isVerifying || isRateLimited}
                  className="h-12 w-full bg-primary hover:bg-primary/90"
                  size="lg"
                  type="button"
                >
                  {isVerifying ? "Verifying..." : "Verify Code"}
                </Button>

                <div className="flex flex-col items-center gap-2">
                  <Button
                    variant="ghost"
                    onClick={handleSendOtp}
                    disabled={timer > 0 || isSubmitting}
                    className="h-8 text-sm text-white/60 hover:bg-white/5 hover:text-white"
                    type="button"
                  >
                    {timer > 0 ? `Resend code in ${timer}s` : "Resend code"}
                  </Button>

                  <Button
                    variant="ghost"
                    onClick={onBackToEmail}
                    className="h-8 text-sm text-white/60 hover:bg-white/5 hover:text-white"
                    type="button"
                  >
                    <ArrowLeft className="mr-2 h-3 w-3" />
                    Use different email
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}

export function AuthModal({ open, onOpenChange }: AuthModalProps) {
  // Always use light logo since we're using dark background
  const logoSrc = "/nudj-logos/Nudj-large-light.png";

  // Standalone component without dialog wrapper
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-black/40 border-white/10 text-white p-0 rounded-2xl backdrop-blur-xl shadow-2xl overflow-hidden">
        <AnimatePresence mode="wait">
          <div className="p-8">
            <DialogHeader className="text-center mb-8">
              <motion.div
                className="mb-6 flex justify-center"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1, duration: 0.5 }}
              >
                <Image
                  src={logoSrc}
                  alt="Nudj"
                  width={80}
                  height={80}
                  priority
                  className="h-20 w-20 object-contain"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                <DialogTitle className="text-3xl font-bold text-white">
                  Welcome to Nudj
                </DialogTitle>
                <DialogDescription className="text-sm text-white/60 mt-2">
                  Build engaged communities with gamification
                </DialogDescription>
              </motion.div>
            </DialogHeader>
            <AuthScreen />
          </div>
        </AnimatePresence>
        <p className="px-8 text-center text-sm text-muted-foreground mb-8">
          By clicking continue, you agree to our{" "}
          <a
            href="/terms"
            className="underline underline-offset-4 hover:text-primary"
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href="/privacy"
            className="underline underline-offset-4 hover:text-primary"
          >
            Privacy Policy
          </a>
          .
        </p>
      </DialogContent>
    </Dialog>
  );
}
